import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import ProductCard from '../components/ProductCard';

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [currentReview, setCurrentReview] = useState(0);
  const [fadeState, setFadeState] = useState('in'); // 'in' or 'out'

  // Auto-rotate reviews with fade effect
  const rotateReview = useCallback(() => {
    if (reviews.length > 1) {
      setFadeState('out');
      setTimeout(() => {
        setCurrentReview((prev) => (prev + 1) % reviews.length);
        setFadeState('in');
      }, 500); // Wait for fade out before changing
    }
  }, [reviews.length]);

  useEffect(() => {
    if (reviews.length > 1) {
      const interval = setInterval(rotateReview, 5000); // Change every 5 seconds
      return () => clearInterval(interval);
    }
  }, [reviews.length, rotateReview]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed data first
        await api.seedData();
        
        const [products, reviewsData] = await Promise.all([
          api.getFeaturedProducts(),
          api.getReviews(),
        ]);
        setFeaturedProducts(products.slice(0, 4));
        setReviews(reviewsData.slice(0, 6)); // Get more reviews for the carousel
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="min-h-screen relative flex items-center hero-gradient overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://res.cloudinary.com/dxp2pmgrv/image/upload/v1771703617/onlyskullz/shuj29fdyhzomhkq0zym.jpg"
            alt="Red skull art"
            className="w-full h-full object-cover object-center opacity-70 md:opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-void/90 via-void/60 to-transparent md:from-void md:via-void/80 md:to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-b from-void/50 via-transparent to-void md:hidden" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-32">
          <div className="max-w-2xl">
            <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter uppercase text-white leading-none mb-6 opacity-0 animate-fade-in animate-delay-100">
              Skull<br />
              <span className="text-cyan-400">Artistry</span>
            </h1>
            <p className="text-lg md:text-xl text-neutral-300 leading-relaxed mb-10 max-w-lg opacity-0 animate-fade-in animate-delay-200">
              Each creation brings a skull, handcrafted with artistic vision bringing in all kinds of materials. Own a piece of extraordinary!
            </p>
            <div className="flex flex-wrap gap-4 opacity-0 animate-fade-in animate-delay-300">
              <Link
                to="/shop"
                data-testid="hero-shop-btn"
                className="bg-cyan-400 text-white px-8 py-4 font-mono uppercase tracking-widest text-sm hover:bg-cyan-400-hover transition-all duration-300 hover:shadow-[0_0_20px_rgba(217,70,239,0.4)] flex items-center gap-2"
              >
                Shop Collection <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/about"
                data-testid="hero-about-btn"
                className="bg-transparent border border-white/20 text-white px-8 py-4 font-mono uppercase tracking-widest text-sm hover:bg-white hover:text-black transition-all duration-300"
              >
                Meet the Artist
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-void">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12">
            <div>
              <span className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-2 block">
                Featured Collection
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-white">
                Signature Pieces
              </h2>
            </div>
            <Link
              to="/shop"
              data-testid="view-all-products"
              className="text-neutral-400 hover:text-white font-mono text-sm uppercase tracking-widest transition-colors flex items-center gap-2 mt-4 md:mt-0"
            >
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="aspect-square bg-void-subtle animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Artist Statement Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-void-paper relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1747696766706-5485b39bf358?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Marble texture"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-4xl mx-auto text-center">
          <span className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-6 block">
            The Vision
          </span>
          <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-white mb-8 leading-tight">
            "Art is the bridge between<br />mortality and eternity"
          </h2>
          <p className="text-lg text-neutral-400 leading-relaxed mb-10 max-w-2xl mx-auto">
            Each skull I create is a meditation on existence—a fusion of ancient symbolism 
            and modern materials. The resin captures light and color in ways that make 
            every piece feel alive, despite its subject matter.
          </p>
          <Link
            to="/about#meet-angel"
            data-testid="read-story-btn"
            className="inline-flex items-center gap-2 text-white font-mono text-sm uppercase tracking-widest border-b border-cyan-400 pb-1 hover:text-cyan-400 transition-colors"
          >
            Read My Story <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-void">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12">
            <div>
              <span className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-2 block">
                Testimonials
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-white">
                What Collectors Say
              </h2>
            </div>
            <Link
              to="/reviews"
              data-testid="view-all-reviews"
              className="text-neutral-400 hover:text-white font-mono text-sm uppercase tracking-widest transition-colors flex items-center gap-2 mt-4 md:mt-0"
            >
              All Reviews <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="h-64 bg-void-subtle animate-pulse max-w-2xl mx-auto" />
          ) : reviews.length > 0 ? (
            <div className="relative max-w-3xl mx-auto">
              {/* Fade-in/out Review */}
              <div 
                className={`transition-opacity duration-500 ease-in-out ${
                  fadeState === 'in' ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <div className="text-center p-8 md:p-12">
                  {/* Stars */}
                  <div className="flex justify-center gap-1 mb-6">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-5 h-5 ${
                          i < reviews[currentReview]?.rating
                            ? 'fill-cyan-400 text-cyan-400'
                            : 'text-neutral-700'
                        }`}
                      />
                    ))}
                  </div>

                  {/* Quote */}
                  <blockquote className="text-xl md:text-2xl text-white leading-relaxed mb-8 font-light">
                    "{reviews[currentReview]?.comment}"
                  </blockquote>

                  {/* Review Images */}
                  {reviews[currentReview]?.images && reviews[currentReview]?.images.length > 0 && (
                    <div className="flex justify-center gap-3 mb-6">
                      {reviews[currentReview].images.map((img, index) => (
                        <div 
                          key={img.url || `review-img-${index}`}
                          className="w-20 h-20 overflow-hidden border border-white/10"
                        >
                          <img
                            src={img.url}
                            alt={img.alt || 'Review photo'}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Author */}
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-12 h-12 bg-cyan-400/20 rounded-full flex items-center justify-center">
                      <span className="text-cyan-400 font-heading font-bold text-lg">
                        {reviews[currentReview]?.customer_name?.charAt(0)}
                      </span>
                    </div>
                    <div className="text-left">
                      <p className="font-heading font-semibold text-white">
                        {reviews[currentReview]?.customer_name}
                      </p>
                      <p className="text-xs text-neutral-500 font-mono uppercase tracking-widest">
                        Verified Buyer
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dot indicators */}
              {reviews.length > 1 && (
                <div className="flex justify-center gap-2 mt-6">
                  {reviews.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setFadeState('out');
                        setTimeout(() => {
                          setCurrentReview(index);
                          setFadeState('in');
                        }, 500);
                      }}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        currentReview === index
                          ? 'bg-cyan-400 w-6'
                          : 'bg-white/20 hover:bg-white/40'
                      }`}
                      aria-label={`Go to review ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p className="text-center text-neutral-400">No reviews yet.</p>
          )}
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 px-6 md:px-12 lg:px-24 bg-void-subtle border-y border-white/5">
        <div className="max-w-2xl mx-auto text-center">
          <h3 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-4">
            Stay in the Loop
          </h3>
          <p className="text-neutral-400 mb-6">
            Be the first to know about new pieces, exclusive releases, and behind-the-scenes content.
          </p>
          <form 
            className="flex flex-col sm:flex-row gap-4" 
            onSubmit={async (e) => {
              e.preventDefault();
              if (!email) {
                toast.error('Please enter your email');
                return;
              }
              setSubscribing(true);
              try {
                await api.subscribe(email);
                toast.success('Thanks for subscribing!');
                setEmail('');
              } catch (error) {
                toast.error('Failed to subscribe. Please try again.');
              }
              setSubscribing(false);
            }}
          >
            <input
              type="email"
              placeholder="Your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="newsletter-email"
              className="flex-1 bg-void border border-white/10 h-14 px-6 text-white placeholder:text-neutral-600 focus:border-cyan-400/50 focus:outline-none transition-colors"
            />
            <button
              type="submit"
              disabled={subscribing}
              data-testid="newsletter-submit"
              className="bg-cyan-400 text-white px-8 h-14 font-mono uppercase tracking-widest text-sm hover:bg-cyan-400-hover transition-colors disabled:opacity-50"
            >
              {subscribing ? 'Subscribing...' : 'Subscribe'}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
